import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Brain, Workflow, GitBranch, Code2, Database, Cloud,
  DollarSign, Activity, MessageSquare, Sparkles, Network,
} from "lucide-react";

const NAV: Array<{ to: string; label: string; icon: typeof Brain; section?: string }> = [
  { to: "/", label: "Overview", icon: LayoutDashboard, section: "Platform" },
  { to: "/ai-stack", label: "AI Stack", icon: Brain },
  { to: "/repos", label: "Code Galaxy", icon: Code2 },
  { to: "/services", label: "Services", icon: Cloud },
  { to: "/topology", label: "Topology", icon: Network },
  { to: "/site-metrics", label: "Site Metrics", icon: Activity },
  { to: "/n8n", label: "n8n Workflows", icon: Workflow, section: "Automation" },
  { to: "/atlassian", label: "Atlassian", icon: GitBranch },
  { to: "/data", label: "Data Layer", icon: Database, section: "Infrastructure" },
  { to: "/health", label: "Health", icon: Activity },
  { to: "/cost", label: "Cost", icon: DollarSign },
  { to: "/chat", label: "Chat", icon: MessageSquare, section: "Tools" },
];

export default function Sidebar() {
  return (
    <aside className="w-[260px] shrink-0 h-screen sticky top-0 border-r border-white/[0.04] bg-bg-900/30 backdrop-blur-xl flex flex-col relative">
      {/* Vertical edge gradient — subtle */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/[0.08] to-transparent pointer-events-none" />
      {/* Logo / brand */}
      <div className="px-6 py-6 border-b border-white/[0.04]">
        <div className="flex items-center gap-3">
          <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-accent-blue via-accent-violet to-accent-cyan bg-[length:200%_100%] animate-gradient-pan flex items-center justify-center shadow-[0_0_24px_rgba(96,165,250,0.35),inset_0_1px_0_rgba(255,255,255,0.20)]">
            <Sparkles size={18} className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-tight">Bracken Admin</div>
            <div className="eyebrow mt-0.5">Mission Control</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((item, i) => (
          <div key={item.to}>
            {item.section && (
              <div className="eyebrow px-3 pt-4 pb-2">{item.section}</div>
            )}
            <NavLink
              to={item.to}
              end={item.to === "/"}
              className={(p: { isActive: boolean }) =>
                `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ease-out ${
                  p.isActive
                    ? "bg-white/[0.06] text-white border border-white/[0.10] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]"
                    : "text-slate-400 hover:text-white hover:bg-white/[0.04] hover:translate-x-0.5 border border-transparent"
                }`
              }
            >
              {(props: { isActive: boolean }) => (
                <>
                  <item.icon
                    size={16}
                    strokeWidth={props.isActive ? 2.25 : 1.75}
                    className={props.isActive ? "text-accent-blue" : ""}
                  />
                  <span className="flex-1">{item.label}</span>
                  {props.isActive && (
                    <motion.div
                      layoutId="active-pill"
                      className="w-1 h-4 rounded-full bg-gradient-to-b from-accent-blue to-accent-violet shadow-[0_0_12px_rgba(96,165,250,0.6)]"
                    />
                  )}
                </>
              )}
            </NavLink>
          </div>
        ))}
      </nav>

      {/* Footer status */}
      <div className="px-4 py-4 border-t border-white/[0.04]">
        <div className="glass px-3 py-2.5 flex items-center gap-2.5">
          <div className="relative">
            <span className="block w-2 h-2 rounded-full bg-accent-emerald" />
            <span className="absolute inset-0 rounded-full bg-accent-emerald animate-ping opacity-60" />
          </div>
          <div className="text-[11px] font-mono">
            <div className="text-slate-300">SYSTEM_ONLINE</div>
            <div className="eyebrow mt-0.5">edge_secured</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
